import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Navigation } from '../../components/layout/Navigation';
import { Footer } from '../../components/layout/Footer';
import { Container } from '../../components/ui/Container';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { getPage } from '../../service/app/app.service';
import aboutMockData from '../../data/about.mock.json';
import './about.css';

type AboutStat = Readonly<{
  label: string;
  value: string;
}>;

type AboutCard = Readonly<{
  title: string;
  body: string;
}>;

type AboutStep = Readonly<{
  title: string;
  description: string;
}>;

type AboutMilestone = Readonly<{
  year: string;
  title: string;
  body: string;
}>;

type AboutPageData = Readonly<{
  kicker: string;
  heroTitle: string;
  heroSubtitle: string;
  heroBadge: string;
  stats: ReadonlyArray<AboutStat>;
  principlesIntro: string;
  principles: ReadonlyArray<AboutCard>;
  milestones: ReadonlyArray<AboutMilestone>;
  learningSystem: ReadonlyArray<AboutStep>;
  teamTitle: string;
  teamBody: string;
  quote: string;
}>;

const ABOUT_MOCK_DATA = aboutMockData as AboutPageData;

const vertexShaderSource = `
  attribute vec2 aPosition;
  varying vec2 vUv;
  void main() {
    vUv = (aPosition + 1.0) * 0.5;
    gl_Position = vec4(aPosition, 0.0, 1.0);
  }
`;

const fragmentShaderSource = `
  precision mediump float;
  uniform float uTime;
  uniform vec2 uResolution;
  varying vec2 vUv;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }

  void main() {
    vec2 uv = (vUv - 0.5) * vec2(uResolution.x / max(uResolution.y, 1.0), 1.0);
    float t = uTime * 0.2;
    float r = length(uv);
    float a = atan(uv.y, uv.x);

    float swirl = sin((a * 4.0) + (r * 9.0) - (t * 3.2));
    float plume = noise(uv * 4.0 + vec2(t, -t * 0.45));
    float ring = smoothstep(0.88, 0.04, r);

    vec3 base = vec3(0.03, 0.07, 0.13);
    vec3 coral = vec3(1.0, 0.56, 0.35);
    vec3 cyan = vec3(0.4, 0.95, 1.0);
    vec3 cream = vec3(1.0, 0.9, 0.75);

    vec3 color = base;
    color += coral * smoothstep(-0.5, 0.8, swirl + plume * 0.45) * 0.42;
    color += cyan * smoothstep(0.1, 0.95, plume + (1.0 - r) * 0.4) * 0.37;
    color += cream * ring * 0.22;
    color += vec3(noise(uv * 8.0 + t) * 0.03);

    gl_FragColor = vec4(color, 0.82);
  }
`;

const createShader = (
  gl: WebGLRenderingContext,
  shaderType: number,
  source: string
): WebGLShader | null => {
  const shader = gl.createShader(shaderType);
  if (!shader) {
    return null;
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
};

const createProgram = (
  gl: WebGLRenderingContext,
  vertexShader: WebGLShader,
  fragmentShader: WebGLShader
): WebGLProgram | null => {
  const program = gl.createProgram();
  if (!program) {
    return null;
  }
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }
  return program;
};

const AboutCanvas: React.FC = () => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
    });
    if (!gl) {
      return undefined;
    }

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!vertexShader || !fragmentShader) {
      return undefined;
    }

    const program = createProgram(gl, vertexShader, fragmentShader);
    if (!program) {
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      return undefined;
    }

    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    const buffer = gl.createBuffer();
    if (!buffer) {
      gl.deleteProgram(program);
      return undefined;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1,
      1, -1,
      -1, 1,
      1, 1,
    ]), gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(program, 'aPosition');
    const timeLocation = gl.getUniformLocation(program, 'uTime');
    const resolutionLocation = gl.getUniformLocation(program, 'uResolution');
    const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.floor(canvas.clientWidth * dpr);
      const height = Math.floor(canvas.clientHeight * dpr);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    };

    let frame = 0;
    const start = performance.now();

    const render = (now: number) => {
      resize();
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

      if (timeLocation) {
        gl.uniform1f(timeLocation, (now - start) / 1000);
      }
      if (resolutionLocation) {
        gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      }
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };

    const loop = (now: number) => {
      render(now);
      frame = window.requestAnimationFrame(loop);
    };

    resize();
    if (reduceMotionQuery.matches) {
      render(start);
    } else {
      frame = window.requestAnimationFrame(loop);
    }

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      window.cancelAnimationFrame(frame);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    };
  }, []);

  return <canvas ref={canvasRef} className="h-full w-full" aria-hidden="true" />;
};

const normalizeAboutData = (raw: unknown): AboutPageData => {
  if (!raw || typeof raw !== 'object') return ABOUT_MOCK_DATA;
  const data = raw as Partial<AboutPageData>;

  return {
    ...ABOUT_MOCK_DATA,
    ...data,
    stats: Array.isArray(data.stats) && data.stats.length ? data.stats : ABOUT_MOCK_DATA.stats,
    principles: Array.isArray(data.principles) && data.principles.length ? data.principles : ABOUT_MOCK_DATA.principles,
    milestones: Array.isArray(data.milestones) && data.milestones.length ? data.milestones : ABOUT_MOCK_DATA.milestones,
    learningSystem: Array.isArray(data.learningSystem) && data.learningSystem.length ? data.learningSystem : ABOUT_MOCK_DATA.learningSystem,
  };
};

export const AboutPage: React.FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['page', 'about'],
    queryFn: async () => {
      const result = await getPage<Partial<AboutPageData>>('about');
      if (result.error || !result.data) {
        return ABOUT_MOCK_DATA;
      }
      return normalizeAboutData(result.data);
    },
    staleTime: 5 * 60 * 1000,
  });

  const about = data || ABOUT_MOCK_DATA;

  return (
    <div className="about-page min-h-screen text-white">
      <Navigation />

      <main className="relative overflow-hidden pb-20 pt-28 md:pt-32">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[620px] opacity-95">
          <AboutCanvas />
        </div>

        <Container>
          {isLoading ? (
            <div className="flex justify-center py-24">
              <Spinner size="xl" />
            </div>
          ) : (
            <>
              <section className="about-panel about-rise relative rounded-[2rem] p-8 md:p-12">
                <p className="about-chip mb-5 inline-flex rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em]">
                  {about.heroBadge}
                </p>
                <p className="text-sm uppercase tracking-[0.18em] text-cyan-100/80">{about.kicker}</p>
                <h1 className="about-title mt-4 max-w-5xl text-5xl font-bold md:text-7xl">
                  {about.heroTitle}
                </h1>
                <p className="mt-6 max-w-4xl text-lg text-slate-100/95 md:text-xl">
                  {about.heroSubtitle}
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link to="/courses">
                    <Button variant="gold" size="lg">
                      View Programs
                    </Button>
                  </Link>
                  <Link to="/instructors">
                    <Button variant="outline" size="lg">
                      Meet Instructors
                    </Button>
                  </Link>
                </div>
              </section>

              <section className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
                {about.stats.map((stat, index) => (
                  <article
                    key={stat.label}
                    className="about-panel about-rise rounded-2xl p-5 text-center"
                    style={{ animationDelay: `${index * 95}ms` }}
                  >
                    <p className="about-title text-3xl text-white">{stat.value}</p>
                    <p className="mt-2 text-sm text-slate-100/85">{stat.label}</p>
                  </article>
                ))}
              </section>

              <section className="mt-16">
                <p className="text-xs uppercase tracking-[0.22em] text-[#99fbff]">Operating Principles</p>
                <h2 className="about-title mt-3 text-4xl md:text-5xl">How We Build System Thinkers</h2>
                <p className="mt-3 max-w-4xl text-slate-100/86">
                  {about.principlesIntro}
                </p>
                <div className="mt-7 grid grid-cols-1 gap-5 md:grid-cols-3">
                  {about.principles.map((principle, index) => (
                    <article
                      key={principle.title}
                      className="about-panel about-rise rounded-2xl p-6"
                      style={{ animationDelay: `${index * 120}ms` }}
                    >
                      <p className="text-sm font-semibold uppercase tracking-wide text-[#ffd4ae]">0{index + 1}</p>
                      <h3 className="mt-3 text-2xl font-bold">{principle.title}</h3>
                      <p className="mt-3 text-slate-100/88">{principle.body}</p>
                    </article>
                  ))}
                </div>
              </section>

              <section className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-5">
                <div className="lg:col-span-3">
                  <div className="about-panel rounded-2xl p-7 md:p-8">
                    <p className="text-xs uppercase tracking-[0.22em] text-[#ffd3af]">Timeline</p>
                    <h2 className="about-title mt-3 text-4xl">Our Growth Story</h2>
                    <div className="mt-6 space-y-4">
                      {about.milestones.map((milestone, index) => (
                        <article
                          key={`${milestone.year}-${milestone.title}`}
                          className="about-rise rounded-xl border border-white/15 bg-slate-950/40 p-5"
                          style={{ animationDelay: `${index * 90}ms` }}
                        >
                          <p className="text-sm font-semibold text-[#ffd3af]">{milestone.year}</p>
                          <h3 className="mt-1 text-xl font-bold">{milestone.title}</h3>
                          <p className="mt-2 text-slate-100/85">{milestone.body}</p>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <div className="about-panel about-float rounded-2xl p-7 md:p-8">
                    <p className="text-xs uppercase tracking-[0.22em] text-[#99fbff]">Learning System</p>
                    <h2 className="about-title mt-3 text-3xl">A Weekly Engine for Progress</h2>
                    <div className="mt-6 space-y-3">
                      {about.learningSystem.map((step, index) => (
                        <article key={step.title} className="rounded-xl border border-white/15 bg-white/5 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-[#99fbff]">Step {index + 1}</p>
                          <h3 className="mt-1 text-lg font-bold text-white">{step.title}</h3>
                          <p className="mt-2 text-sm text-slate-100/80">{step.description}</p>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              <section className="mt-16">
                <div className="about-panel rounded-2xl p-8 md:p-10">
                  <p className="about-title text-3xl leading-tight text-[#ffe2bd] md:text-4xl">
                    “{about.quote}”
                  </p>
                  <h3 className="mt-7 text-2xl font-bold">{about.teamTitle}</h3>
                  <p className="mt-3 max-w-4xl text-slate-100/86">{about.teamBody}</p>
                </div>
              </section>
            </>
          )}
        </Container>
      </main>

      <Footer />
    </div>
  );
};
